import React from 'react';
import { ResourceBookingSchema } from '../../data/types';
import {
  capitalizeString,
  capitalizeWords,
  getBookingStatusColor,
  getDirectLinkFromGoogleDriveSharedLink,
  styleThousandCommas,
  formatBookingDateTime,
  formatBookingTimeRange,
  getBookingDurationHours,
} from '../../utils/helpers';
import { ReactComponent as LocationIcon } from '../../icons/locationIcon.svg';

type BookedResourceListRowProps = {
  booking: ResourceBookingSchema;
  onViewDetails?: (booking: ResourceBookingSchema) => void;
  onEditDetails?: (booking: ResourceBookingSchema) => void;
};

const BookedResourceListRow: React.FC<BookedResourceListRowProps> = ({
  booking,
  onViewDetails,
  onEditDetails,
}) => {
  const statusColor = getBookingStatusColor(booking?.status || 'pending');
  const duration = getBookingDurationHours(booking);
  const canEdit = !(booking.status === 'completed' || booking.status === 'canceled');

  return (
    <div className="flex flex-col gap-3 p-4 border border-[#E9E9E9] rounded-xl bg-white">
      <div className="flex flex-row items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 border border-[#E9E9E9] rounded-lg flex items-center justify-center bg-white">
          {getDirectLinkFromGoogleDriveSharedLink(booking?.resource_logo) ? (
            <img
              className="h-8 w-8 object-contain"
              src={getDirectLinkFromGoogleDriveSharedLink(booking.resource_logo)}
              alt={booking.resource_name}
            />
          ) : (
            <span className="text-[#013847] text-xs font-bold">
              {(booking.resource_name || '??').substring(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-raleway font-semibold text-[#013847]">{booking.resource_name || '—'}</p>
          <p className="font-raleway text-xs text-[#8A8A8A] flex items-center mt-0.5">
            <LocationIcon className="w-3 h-3 mr-1 flex-shrink-0" />
            {booking.resource_city ? `${booking.resource_city}, ` : ''}
            {booking.resource_state || 'Nationwide'}
            {booking.resource_type && (
              <span className="ml-2 capitalize">· {booking.resource_type}</span>
            )}
          </p>
        </div>
        {booking.status && (
          <span
            className="px-2 py-1 rounded-md text-[10px] font-raleway font-bold border capitalize flex-shrink-0"
            style={{
              borderColor: statusColor.primaryTextColor,
              color: statusColor.primaryTextColor,
              backgroundColor: statusColor.bgColor,
            }}
          >
            {capitalizeWords(booking.status.replace(/_/g, ' '))}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm font-raleway">
        <div>
          <p className="text-[10px] uppercase text-[#8A8A8A] font-semibold">Date</p>
          <p className="text-[#013847]">{formatBookingDateTime(booking, 'MM/DD/YYYY')}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-[#8A8A8A] font-semibold">Time</p>
          <p className="text-[#013847] text-xs">{formatBookingTimeRange(booking)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-[#8A8A8A] font-semibold">Duration</p>
          <p className="text-[#013847]">{duration > 0 ? `${duration.toFixed(1)}h` : '—'}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-[#8A8A8A] font-semibold">Plan / Cost</p>
          <p className="text-[#013847] text-xs">
            {booking.plan_rate != null ? `$${booking.plan_rate}` : '—'}
            {booking.plan_recurrence && (
              <span className="text-[#8A8A8A]"> / {capitalizeString(booking.plan_recurrence)}</span>
            )}
            {booking.total_cost != null && (
              <span className="block">
                Total: ${styleThousandCommas(booking.total_cost.toFixed(2))}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex flex-row gap-2">
        {canEdit && (
          <button
            type="button"
            className="flex-1 py-2.5 text-sm font-raleway font-semibold text-[#013847] border border-[#E9E9E9] rounded-lg"
            onClick={() => onEditDetails?.(booking)}
          >
            Edit
          </button>
        )}
        <button
          type="button"
          className="flex-1 py-2.5 text-sm font-raleway font-semibold text-white bg-[#013847] rounded-lg"
          onClick={() => onViewDetails?.(booking)}
        >
          View
        </button>
      </div>
    </div>
  );
};

export default BookedResourceListRow;
